import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Brain, Loader2, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { startBatchAnalysis, getBatchProgress, getJobBatchStatus } from '../services/api';
import { toast } from 'sonner';

export const BatchProgressTracker = ({ jobId, totalResumes, analyzedCount, onComplete, readOnly = false }) => {
  const [batchId, setBatchId] = useState(null);
  const [progress, setProgress] = useState(null);
  const [starting, setStarting] = useState(false);
  const intervalRef = useRef(null);

  const hasUnanalyzed = totalResumes > analyzedCount;

  // Check for existing running batch on mount
  useEffect(() => {
    checkExistingBatch();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [jobId]);

  const checkExistingBatch = async () => {
    try {
      const batch = await getJobBatchStatus(jobId);
      if (batch && batch.status === 'running') {
        setBatchId(batch.id);
        setProgress(batch);
        startPolling(batch.id);
      }
    } catch (e) { /* no active batch */ }
  };

  const startPolling = (id) => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(async () => {
      try {
        const p = await getBatchProgress(id);
        setProgress(p);
        if (p.status === 'completed') {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          toast.success(`Analysis complete! ${p.processed - p.failed} candidates analyzed.`);
          onComplete?.();
        }
      } catch (e) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 2000);
  };

  const handleStart = async () => {
    try {
      setStarting(true);
      const result = await startBatchAnalysis(jobId);
      if (result.batch_id) {
        setBatchId(result.batch_id);
        setProgress({ total: result.total, processed: 0, failed: 0, status: 'running', current_candidate: '' });
        startPolling(result.batch_id);
        toast.info(`Starting analysis of ${result.total} candidates...`);
      } else {
        toast.info(result.message || 'All candidates already analyzed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start batch analysis');
    } finally {
      setStarting(false);
    }
  };

  if (!hasUnanalyzed && !progress) return null;

  const isRunning = progress?.status === 'running';
  const isComplete = progress?.status === 'completed';
  const pct = progress ? Math.round((progress.processed / Math.max(progress.total, 1)) * 100) : 0;

  return (
    <Card className={`border-indigo-500/20 ${isRunning ? 'bg-indigo-500/5 border-indigo-500/40' : 'bg-indigo-500/5'}`} data-testid="batch-progress-tracker">
      <CardContent className="py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {isRunning ? (
              <div className="relative">
                <Brain className="h-5 w-5 text-indigo-500" />
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-indigo-500 rounded-full animate-pulse" />
              </div>
            ) : isComplete ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            ) : (
              <Brain className="h-5 w-5 text-indigo-500" />
            )}
            <div className="min-w-0">
              {isRunning ? (
                <>
                  <p className="font-medium text-sm">
                    Analyzing candidates... {progress.processed}/{progress.total}
                  </p>
                  {progress.current_candidate && (
                    <p className="text-xs text-muted-foreground truncate">
                      Current: {progress.current_candidate}
                    </p>
                  )}
                </>
              ) : isComplete ? (
                <>
                  <p className="font-medium text-sm text-emerald-600">
                    Analysis complete! {progress.processed - progress.failed} candidates processed
                  </p>
                  {progress.failed > 0 && (
                    <p className="text-xs text-amber-500 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> {progress.failed} failed
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="font-medium text-sm">
                    {totalResumes - analyzedCount} resumes ready for analysis
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Run AI analysis to rank candidates with real-time progress
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isRunning ? (
              <Badge variant="secondary" className="gap-1.5 animate-pulse">
                <Loader2 className="h-3 w-3 animate-spin" />
                {pct}%
              </Badge>
            ) : isComplete ? (
              <Button variant="ghost" size="sm" onClick={() => { setProgress(null); setBatchId(null); onComplete?.(); }} data-testid="dismiss-batch-btn">
                <RefreshCw className="h-4 w-4 mr-1" /> Refresh
              </Button>
            ) : !readOnly && (
              <Button onClick={handleStart} disabled={starting} data-testid="start-batch-analysis-btn">
                {starting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Brain className="h-4 w-4 mr-2" />}
                Analyze All
              </Button>
            )}
          </div>
        </div>

        {isRunning && (
          <div className="mt-4 space-y-2">
            <Progress value={pct} className="h-2" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{progress.processed} of {progress.total} processed</span>
              {progress.failed > 0 && <span className="text-amber-500">{progress.failed} failed</span>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
